import {
  identity,
  isEmpty,
  map,
  memoizeWith,
  pipe,
  partial,
  prop,
  propEq,
  values,
} from "ramda";
import { createSelector } from "reselect";
import { RouteComponentProps } from "react-router";
import { Maybe } from "true-myth";

import { dateParamToDate, sixDaysLater } from "helpers/dates";
import {
  encodePublicContainerUrl,
  getOrderedPeople,
  mergeUnavails,
  sortPeopleShiftsByCategory,
} from "helpers/schedule/scheduling_container";
import {
  applyFilters,
  getCategoriesWithRoles,
} from "helpers/schedule/action_bar/filters";
import { posUsersToPeople } from "helpers/transforms/pos_users";
import { mergePeopleIntoSchedule } from "helpers/transforms/schedule";
import { IRootStateProps, IUsersByUuid } from "types/reducers";
import { ISchedule, IPeople, IPosUser, IShift } from "types/schedules";

interface IOwnProps extends RouteComponentProps<{ permalink: string }> {}

const selectScheduling = (state: IRootStateProps) => state.scheduling;
const selectFilters = (state: IRootStateProps) => state.filters;

const selectRawSchedule = createSelector(selectScheduling, prop("schedule"));

const selectUsers = createSelector(selectScheduling, prop("users"));

const selectBlockModalData = createSelector(
  selectScheduling,
  prop("timeBlockModalData")
);

const selectUnavail = createSelector(selectScheduling, prop("unavailBlocks"));

const selectStartEndDates = createSelector(selectRawSchedule, (schedule) => {
  const { startDate, endDate } = schedule;
  return { startDate, endDate };
});

const selectSchedule = createSelector(
  selectRawSchedule,
  selectUsers,
  (schedule, users) => {
    // Schedule data may contain stale assignee IPosUser records.
    // Replace assignee data with the freshest records (from `users`),
    // as long as we have some user data.
    const syncedSchedule = isEmpty(users)
      ? schedule
      : syncAssignees(schedule, users);
    // Create people records for all users, so they are part of the schedule
    // even when they have no shifts
    const userPeople = posUsersToPeople(users);
    return mergePeopleIntoSchedule(userPeople, syncedSchedule);
  }
);

function syncAssignees(schedule: ISchedule, users: IUsersByUuid): ISchedule {
  const peopleWithSyncedAssignees = map(
    (person) =>
      person.assignee.match({
        Just: (assignee) => {
          // For a given assignee, see if we have a fresher record in users
          const freshAssignee = Maybe.of(users[assignee.uuid]);
          if (freshAssignee.isJust()) {
            return {
              ...person,
              assignee: freshAssignee,
              shifts: syncShiftRoles(
                person.shifts,
                freshAssignee.unsafelyUnwrap()
              ),
            };
          }
          return person;
        },
        Nothing: () => person,
      }),
    schedule.people
  );
  return {
    ...schedule,
    people: peopleWithSyncedAssignees,
  };
}

function syncShiftRoles(shifts: IShift[], assignee: IPosUser): IShift[] {
  return map((shift) => {
    const foundRole = assignee.roles.find(propEq("uuid", shift.role.uuid));
    if (foundRole) {
      return {
        ...shift,
        role: foundRole,
      };
    }
    return shift;
  }, shifts);
}

interface IInteractiveView {
  interactiveView: boolean;
}
const selectInteractiveView = (state: {}, ownProps: IInteractiveView) =>
  ownProps.interactiveView;

const selectOrderedSchedule = createSelector(
  selectSchedule,
  selectFilters,
  selectUnavail,
  selectInteractiveView,
  (schedule, filters, unavailBlocks, interactiveView): ISchedule => {
    let orderedPeople = pipe(
      // Filter people (only in interactive view)
      (people: IPeople) =>
        interactiveView ? applyFilters(filters, people) : people,
      partial(getOrderedPeople, [interactiveView]),
      sortPeopleShiftsByCategory
    )(schedule.people);

    if (interactiveView && unavailBlocks.length) {
      orderedPeople = mergeUnavails(orderedPeople, unavailBlocks);
    }
    return {
      ...schedule,
      people: orderedPeople,
    };
  }
);

const selectCategoriesWithRoles = createSelector(selectUsers, (users) =>
  getCategoriesWithRoles(values(users))
);

const selectUnavailByUuid = (uuid: string) =>
  createSelector(selectUnavail, (unavails) => {
    return unavails.find(propEq("uuid", uuid));
  });

const memoPermalinkMaybe = memoizeWith(identity, (match) =>
  Maybe.of(match.params.permalink)
);
const selectRoutePermalink = (state: IRootStateProps, ownProps: IOwnProps) =>
  memoPermalinkMaybe(ownProps.match);

const selectPublicURL = createSelector(
  selectRoutePermalink,
  selectStartEndDates,
  (permalink, { startDate, endDate }) =>
    encodePublicContainerUrl({
      permalink,
      startDate,
      endDate,
    })
);

const memoSearchParams = memoizeWith(
  identity,
  (search) => new URLSearchParams(search)
);
const selectSearchParams = (state: IRootStateProps, ownProps: IOwnProps) =>
  memoSearchParams(ownProps.location.search);

const selectDateParams = createSelector(selectSearchParams, (params) => {
  const startDateParam: Maybe<string> = Maybe.of(params.get("startDate"));
  const endDateParam: Maybe<string> = Maybe.of(params.get("endDate"));
  const dayViewParam: Maybe<string> = Maybe.of(params.get("dayView"));

  const [startDate, endDate, dayView]: Array<Maybe<Date>> = map(
    dateParamToDate,
    [startDateParam, endDateParam, dayViewParam]
  );
  // Find a valid end date param, end of the week beginning on `start`
  const validEnd = endDate.or(startDate.map(sixDaysLater));
  return { startDate, endDate: validEnd, dayView };
});

export {
  selectUsers,
  selectBlockModalData,
  selectCategoriesWithRoles,
  selectDateParams,
  selectFilters,
  selectInteractiveView,
  selectOrderedSchedule,
  selectPublicURL,
  selectRawSchedule,
  selectRoutePermalink,
  selectSchedule,
  selectScheduling,
  selectSearchParams,
  selectStartEndDates,
  selectUnavailByUuid,
  IInteractiveView,
};
